import { EventType, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';

import { loginRequest, msalConfig, refreshTokenRequest } from '../msalConfig';
import { throttle } from '../utils';
import { REFRESH_TOKEN_DELAY } from './constants';

class AuthService {
  private msalInstance: PublicClientApplication | undefined;

  public initAuthService = async (): Promise<void> => {
    this.msalInstance = new PublicClientApplication(msalConfig);
    await this.msalInstance.initialize();
    this.msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.HANDLE_REDIRECT_END
      ) {
        const currentAccounts = this.msalInstance?.getAllAccounts();
        if (currentAccounts && currentAccounts.length > 0) {
          this.msalInstance?.setActiveAccount(currentAccounts[0]);
        }
      }
    });
  };

  public getMsalInstance = (): PublicClientApplication => this.msalInstance!;

  public isAuthenticated = (): boolean => (this.msalInstance?.getAllAccounts().length || 0) > 0;

  public getAccessToken = async (): Promise<[tokenType: string, accessToken: string]> => {
    let token = '';
    let type = '';

    const account = this.msalInstance?.getActiveAccount() || this.msalInstance?.getAllAccounts()[0];
    if (!account) {
      console.error('No active account set. Cannot acquire token silently.');
      return [type, token];
    }

    await this.msalInstance!.acquireTokenSilent({ ...loginRequest, account })
      .then((response) => {
        token = response.accessToken;
        type = response.tokenType;
      })
      .catch((err) => {
        console.error(err);
        if (err instanceof InteractionRequiredAuthError) {
          this.handleLogout();
        }
      });

    return [type, token];
  };

  public throttledRefreshToken = throttle(() => {
    const account = this.msalInstance?.getActiveAccount() || this.msalInstance?.getAllAccounts()[0];
    if (!account) return;
    this.msalInstance?.acquireTokenSilent({ ...refreshTokenRequest, account }).catch(() => this.handleLogout());
  }, REFRESH_TOKEN_DELAY);

  public handleLogout = (): void => {
    const idTokenHint = this.msalInstance?.getAllAccounts()[0].idToken;
    !!idTokenHint &&
      this.msalInstance
        ?.logoutRedirect({
          idTokenHint,
        })
        .catch((err) => console.error('Logout error:', err));
  };
}

export const authService = new AuthService();
